<template>
  <div :class="['bank-email-form', { 'with-error': emailError }]">
    <h2 class="title">Jetzt anmelden oder registrieren</h2>
    <p class="bank-email-form__text">
      Bitte geben Sie Ihre E-Mail Adresse ein und melden Sie sich in Ihrem
      persönlichen Kunden-Konto an. Im Kunden-Konto können Sie jederzeit Ihre
      Bestellung verwalten und den Status einsehen. Sie finden dort alle
      Neuigkeiten und Informationen zu Ihrem Online-Kauf.
    </p>
    <div
      v-if="orderCreationError === 'unprocessable_entity'"
      class="email-error-tooltip"
    >
      <p class="title">Ups, eine kleine Störung ist aufgetreten.</p>
      Versuchen Sie eine andere E-Mail-Adresse oder kontaktieren
      <br />
      Sie uns telefonisch unter:
      <a href="tel:+4902119462550">+49 (0)211-9462 55-0</a>
    </div>
    <div
      v-if="orderCreationError === 'signin_required'"
      class="email-error-tooltip"
    >
      <p class="title">E-Mail Adresse existiert bereits!</p>
      Ihre E-Mail Adresse {{ email }} befindet sich bereits in unserem System.
      Wir haben Ihnen erneut eine Bestätigungs-E-Mail gesendet. Bitte
      verifizieren Sie Ihre E-Mail Adresse durch Klicken auf den Link in der
      E-Mail. Anschließend können Sie den Bestellprozess fortsetzen.
    </div>
    <div
      v-if="orderCreationError === 'validation_errors'"
      class="email-error-tooltip"
    >
      <p class="title">E-Mail Adresse existiert bereits!</p>
      {{ orderCreationErrorDetails }}
    </div>
    <form>
      <label :class="['input-container', { 'with-error': emailError }]">
        <input v-model="email" type="text" placeholder="E-Mail Adresse" />
        <p v-if="emailError" class="error">{{ emailErrorMessage }}</p>
      </label>
      <p v-if="this.isPasswordAllowed" class="title">
        Zu dieser E-Mail Adresse existiert bereits ein Kunden-Konto. Bitte
        melden Sie sich an.
      </p>
      <label
        v-if="this.isPasswordAllowed"
        :class="['input-container second', { 'with-error': passwordError }]"
      >
        <input v-model="password" type="password" placeholder="Passwort" />
        <p v-if="passwordError" class="error">{{ passwordErrorMessage }}</p>
      </label>
      <p v-if="this.isPasswordAllowed" class="link-container">
        <a href="https://suche.pkw.de/passwort/editieren"
          >Passwort vergessen?</a
        >
      </p>
      <label
        :class="[
          'bank-email-form__checkbox',
          { 'with-error': agreementsError },
        ]"
      >
        <div>
          <div class="custom-checkbox">
            <input
              v-model="agreements"
              id="financing-checkbox"
              type="checkbox"
            />
            <label for="financing-checkbox" />
          </div>
          <p>
            Ja, ich stimme den
            <button type="button" @click="openTermsAndServicesModal">
              AGB
            </button>
            und den
            <button type="button" @click="openPolicyModal">
              Datenschutzbestimmungen
            </button>
            von PKW.de zu.
            <!--            <button type="button" @click="openCreditModal">Bonitätsprüfung</button> zu.-->
          </p>
        </div>
        <p v-if="agreementsError" class="error">Dies ist ein Pflichtfeld.</p>
      </label>
      <button class="btn" type="submit" @click="startBankingProcess">
        {{ editMode ? "Änderungen übernehmen" : "Nächster Schritt" }}
      </button>
    </form>
    <div id="ratingWidget" style="margin-top:40px;"></div>
    <TermsAndServicesModal
      :is-open="showTermsAndServicesModal"
      @close="closeTermsAndServicesModal"
    />
    <ConfidentialPolicyModal
      :is-open="showPolicyModal"
      @close="closePolicyModal"
    />
    <CreditCheckModal :is-open="showCreditModal" @close="closeCreditModal" />
  </div>
</template>

<script>
import TermsAndServicesModal from "@/components/modals/TermsAndServicesModal";
import ConfidentialPolicyModal from "@/components/modals/ConfidentialPolicyModal";
import CreditCheckModal from "@/components/modals/CreditCheckModal";
import { logIn } from "@/api/auth";

export default {
  components: {
    TermsAndServicesModal,
    ConfidentialPolicyModal,
    CreditCheckModal,
  },
  props: ["car", "orderCreationError", "orderCreationErrorDetails", "editMode"],
  data() {
    return {
      showTermsAndServicesModal: false,
      showPolicyModal: false,
      showCreditModal: false,
      email: "",
      password: "",
      emailError: false,
      passwordError: false,
      emailErrorMessage: "",
      passwordErrorMessage: "",
      agreements: false,
      agreementsError: false,
      deliveryType: 1,
      isSuccessLogin: false,
      token: null,
    };
  },
  computed: {
    isDesktop() {
      return window.innerWidth > 990;
    },
    carId() {
      return this.$route.query.carId || "";
    },
    isPasswordAllowed() {
      return this.orderCreationError === "unauthorized";
    },
  },
  watch: {
    email(val) {
      if (val && this.emailError) {
        this.emailError = false;
        this.emailErrorMessage = "";
      }
    },
    password(val) {
      if (val && this.passwordError) {
        this.passwordError = false;
        this.passwordErrorMessage = "";
      }
    },
    agreements(val) {
      if (val && this.agreementsError) {
        this.agreementsError = false;
      }
    },
  },
  mounted() {
    window.dataLayer.push({
      event: "order_step",
      step_number: "0",
    });
    this.addRatingWidget();
  },
  methods: {
    addRatingWidget() {
      const googleRating = document.createElement("script");
      googleRating.async = true;
      googleRating.onload = function() {
        getWidget(
          "https://meinebewertungen.pkw.de",
          "/de",
          "kY7Y2P",
          googleRating,
          { id: 3389 }
        );
      };
      googleRating.src = "https://meinebewertungen.pkw.de/js/widget.js";
      const container = document.getElementById("ratingWidget");
      container.appendChild(googleRating);
    },
    openTermsAndServicesModal() {
      this.showTermsAndServicesModal = true;
    },
    closeTermsAndServicesModal() {
      this.showTermsAndServicesModal = false;
    },
    openPolicyModal() {
      this.showPolicyModal = true;
    },
    closePolicyModal() {
      this.showPolicyModal = false;
    },
    openCreditModal() {
      this.showCreditModal = true;
    },
    closeCreditModal() {
      this.showCreditModal = false;
    },
    startBankingProcess(e) {
      e.preventDefault();
      const isValid = this.validateForm();
      const isValidUser = this.validateUser();

      if (isValid && isValidUser) {
        const params = {
          email: this.email,
          car_id: this.carId,
        };

        this.$emit("startOrderProcess", params);
      }
    },
    validateUser() {
      let valid = true;

      if (!this.agreements) {
        this.agreementsError = true;
        valid = false;
      } else {
        this.agreementsError = false;
      }

      if (this.isPasswordAllowed && this.agreements) {
        logIn(this.email, this.password)
          .then((r) => {
            const { token } = r;

            const params = {
              email: this.email,
              car_id: this.carId,
              token,
            };

            this.$emit("startOrderProcess", params);
          })
          .catch(() => {
            valid = false;
            this.passwordError = true;
            this.passwordErrorMessage = "Eingegebene Daten ungültig?";
          });
      }

      return valid;
    },
    validateForm() {
      let noError = true;
      if (!this.email) {
        this.emailError = true;
        this.emailErrorMessage = "Dies ist ein Pflichtfeld.";
        noError = false;
      } else if (!this.checkIfEmailValid()) {
        this.emailError = true;
        this.emailErrorMessage =
          "Bitte geben Sie eine gültige E-Mail Adresse ein";
        noError = false;
      } else {
        this.emailError = false;
      }
      if (!this.agreements) {
        this.agreementsError = true;
        noError = false;
      } else {
        this.agreementsError = false;
      }
      if (noError) {
        this.emailError = false;
        this.agreementsError = false;
      }
      return noError;
    },
    checkIfEmailValid() {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(this.email).toLowerCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.bank-email-form {
  position: relative;
  padding: 30px 0.625rem;
  margin: 0 auto;
  max-width: 600px;

  .btn {
    width: 100%;
  }

  .bank-email-form__text {
    line-height: 24px;
  }
  .video {
    margin-top: 30px;
    h3 {
      margin-bottom: 10px;
    }
  }
  h2 {
    margin-bottom: 10px;
    padding: 0;
    font-size: 18px;
    font-weight: 700;
  }
  &__text {
    margin-bottom: 15px;
  }
  &__checkbox {
    margin-bottom: 20px;
    display: block;
    > div {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 20px;
    }
    input[type="checkbox"] {
      margin-right: 10px;
      min-height: 25px;
    }
    a,
    button {
      color: $red;
      margin: 0;
      padding: 0;
    }
    p {
      margin-left: 20px;
    }
    &.with-error {
      .error {
        margin-left: 0;
        padding: 5px 0;
        font-size: 12px;
        line-height: 12px;
        color: $red;
      }
      input[type="checkbox"] + label:before {
        border-color: $red;
      }
    }
  }
  @include breakpoint(medium) {
    margin: 0;
  }
  @include breakpoint(desktop) {
    padding: 0;

    &__text {
      margin-bottom: 25px;
    }

    h2 {
      font-size: 30px;
      margin-bottom: 50px;
      position: relative;
      display: inline-block;

      &:after {
        content: "";
        display: block;
        position: absolute;
        bottom: -25px;
        left: 50%;
        transform: translateX(-50%);
        background: $red;
        height: 2px;
        width: 100px;
      }
    }

    .video {
      margin-top: 60px;
      h3 {
        font-size: 25px;
        margin-bottom: 30px;
      }
    }
  }

  .email-error-tooltip {
    position: relative;
    padding: 20px 30px;
    margin-bottom: 20px;
    max-width: 450px;
    background: $white;

    z-index: 100;
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.3);

    @include breakpoint(desktop) {
      min-width: 450px;
      position: absolute;
      left: 100%;
      margin-bottom: 0;
      transform: translateY(-35%);
    }

    .title {
      color: $darkgray;
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 10px;
    }

    &:before {
      content: "";
      position: absolute;
      width: 31px;
      height: 70px;
      z-index: 1;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      background: #fff;

      display: none;
      @include breakpoint(desktop) {
        display: block;
      }
    }

    &:after {
      content: "";
      position: absolute;
      width: 40px;
      height: 40px;
      left: -20px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      //z-index: -1;
      background: #fff;
      box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.3);

      display: none;
      @include breakpoint(desktop) {
        display: block;
      }
    }
  }

  .link-container {
    text-align: right;
    margin-bottom: 10px;
    font-size: 14px;

    a {
      color: $red;
    }
  }

  .input-container.second {
    margin-bottom: 10px;
  }
}

.email-error-tooltip a {
  color: $red;
}
</style>
